import { Box } from 'grommet';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AgoroMap } from './components/Map/AgoroMap';
import './Map.style.css';
import { getJourneyByIdAction, getProducerByIdAction, getProducersListAndFieldGroupsAction } from '../../redux/actions/producer-action';
import {
  checkFieldLocation,
  getClassificationLayers,
  getFields,
  getFieldsValidations,
  getLastFieldNum,
  getShapefileStatus,
  setFinishedDrawingFields,
  setTotalFieldsArea,
  verifyNearFields,
  verifyOverlap,
} from '../../redux/actions/map-actions/field/field-action';
import {
  checkIfContractExists,
  clearData,
  getContractInSF,
  getContractVersion,
  getEstimate,
  getMapState,
  getTrainingRegions,
  setShowCloserFields,
} from '../../redux/actions/map-actions/map-action';
import { producerStatusAhead } from '../../redux/actions/map-actions/field/field-helper';
import { getPracticesOnContract } from '../../redux/actions/map-actions/contract-action';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../redux';

export const MapPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { journeyId } = useParams();
  const { brFinalizeBoundaries, upsellingBoundaryTool, shouldLdAllowBoundaryEdit } = useFlags();
  const [showUpsellingBanner, setShowUpsellingBanner] = useState<boolean>(false);
  const user = useAppSelector((state) => state.user.userData);
  const salesforceId = useAppSelector((state) => state.producer.journeyInformation?.opportunityId ?? state.producer.journeyInformation?.leadId);
  const countryLoaded = useAppSelector((state) => !!state.producer.producerInformation?.country);
  const country = useAppSelector((state) => state.producer.producerInformation?.country ?? 'us');
  const producer = useAppSelector((state) => state.producer.producerInformation);

  const fields = useAppSelector((state) => state.map.fields);
  const loadingFields = useAppSelector((state) => state.map.loadingFields);
  const fieldsToCheckIntersection = useAppSelector((state) => state.map.fieldsToCheckIntersection);
  const fieldsToCheckLocation = useAppSelector((state) => state.map.fieldsToCheckLocation);
  const checkOverlaps = useAppSelector((state) => state.map.checkOverlaps);
  const shapefileStatus = useAppSelector((state) => state.map.shapefileStatus);
  const loadingShapefileStatus = useAppSelector((state) => state.map.loadingShapefileStatus);
  const showCloserFields = useAppSelector((state) => state.map.showCloserFields);
  const estimate = useAppSelector((state) => state.map.estimate);
  const mode = useAppSelector((state) => state.map.mode);
  const initialLoad = useAppSelector((state) => state.map.initialLoad);
  const isChannelPartner = user?.channelPartnerId;

  useEffect(() => {
    if (!producer) {
      return;
    }
    dispatch(getPracticesOnContract(producer, country));
    setShowUpsellingBanner(
      upsellingBoundaryTool &&
        producer?.caseStatus === 'Document & Data Collection' &&
        !!producer?.caseStatusHistory?.find((i) => i.status === 'In Support') &&
        user?.country === 'US',
    );
  }, [upsellingBoundaryTool, producer]);

  useEffect(() => {
    dispatch(getProducersListAndFieldGroupsAction());
    return () => {
      dispatch({ type: 'CLEAR_PRODUCER_FILTERS' });
    };
  }, [dispatch]);

  useEffect(() => {
    if (salesforceId && (!producer || producer?.opportunityId !== salesforceId)) {
      dispatch(getProducerByIdAction(salesforceId));
    }
  }, [salesforceId]);

  useEffect(() => {
    if (journeyId?.length === 12) {
      dispatch(getJourneyByIdAction(journeyId));
    } else {
      dispatch(getProducerByIdAction(journeyId, true));
    }
  }, []);

  useEffect(() => {
    dispatch(getFields(journeyId));
    dispatch(getTrainingRegions(journeyId));
    dispatch(getClassificationLayers(journeyId));
    dispatch(getShapefileStatus(journeyId));
    dispatch(getLastFieldNum(journeyId));
    return () => {
      dispatch(clearData());
    };
  }, []);

  useEffect(() => {
    if (initialLoad) {
      return;
    }
    if (!loadingFields && !loadingShapefileStatus && countryLoaded) {
      dispatch(getMapState(fields, shapefileStatus, country, producer, isChannelPartner, brFinalizeBoundaries, shouldLdAllowBoundaryEdit));
      if ((country === 'br' && !producerStatusAhead(producer.caseStatus, brFinalizeBoundaries)) || country === 'us') {
        dispatch(getEstimate(journeyId));
      } else if (country === 'br') {
        dispatch(setShowCloserFields(false));
      }
    }
  }, [fields, loadingFields, loadingShapefileStatus, shapefileStatus, producer, country, countryLoaded, initialLoad]);

  useEffect(() => {
    if (estimate) {
      dispatch(getContractVersion(estimate.contractVersionId));
      if (country === 'br') {
        dispatch(checkIfContractExists(journeyId, estimate.id));
      }
    } else if (estimate !== undefined) {
      dispatch(getContractInSF(producer.opportunityId));
    }
  }, [estimate]);

  useEffect(() => {
    if (!journeyId || !fields || !checkOverlaps) {
      return;
    }

    dispatch(setTotalFieldsArea(fields ?? []));

    if (isChannelPartner) {
      return;
    }

    dispatch(verifyOverlap(journeyId, fields));
    if (showCloserFields) {
      dispatch(verifyNearFields(journeyId, fields, t));
    }
  }, [fields]);

  useEffect(() => {
    dispatch(setFinishedDrawingFields());
  }, [mode]);

  useEffect(() => {
    if (!fieldsToCheckLocation.length) {
      return;
    }

    dispatch(checkFieldLocation(fieldsToCheckLocation[0]));
  }, [fieldsToCheckLocation]);

  useEffect(() => {
    if (!fieldsToCheckIntersection.length) {
      return;
    }

    dispatch(getFieldsValidations(fieldsToCheckIntersection, fields));
  }, [fieldsToCheckIntersection]);

  return (
    <Box className={'agoro-map-wrapper'} style={{ height: '100%' }}>
      {showUpsellingBanner && (
        <Box
          style={{
            width: '100%',
            height: '68px',
            backgroundColor: '#D03450',
            color: '#fff',
            fontFamily: 'Avenir',
            fontSize: '15px',
            fontStyle: 'normal',
            fontWeight: 900,
            lineHeight: '20px',
            letterSpacing: '1.12px',
            justifyContent: 'center',
            // position: 'absolute'
          }}>
          You are on the Upselling flow. Every change made will be considered an addition to all previously inputted information. To edit past data
          collection inputs, please get in touch with the Product Team.
        </Box>
      )}
      <Box
        style={{
          height: '100%',
          width: '100%',
          position: 'relative',
        }}>
        <AgoroMap />
      </Box>
    </Box>
  );
};
